import { useState, useEffect, useMemo, Fragment, useRef } from 'react'
import { useLazyQuery, useMutation, gql } from '@apollo/client'
import { useTable, useFilters } from 'react-table'
import { toast } from 'react-toastify'
import { Popover, Transition } from '@headlessui/react'

import { GET_INVITES } from '../services/admin'
import { FacilAddPointsDialog } from '../components'
import { ROLE_PARTICIPANT } from '../constants'
import { ReactComponent as FabStarIcon } from '../assets/icons/fab-star-icon.svg'
import { ReactComponent as FabCloseIcon } from '../assets/icons/fab-close-icon.svg'
import { ReactComponent as AddCircleIcon } from '../assets/icons/add-circle-icon.svg'

const GET_USERS = gql`
  query GetUser {
    users(first: 1000) {
      edges {
        node {
          databaseId
          firstName
          lastName
          email
          points
        }
      }
    }
  }
`
export const UPDATE_USER_POINTS_MUTATION = gql`
  mutation UpdateUserPoints($userId: Int!, $operation: String!, $bonus: Int!) {
    updateUserPoints(
      input: { userId: $userId, operation: $operation, bonus: $bonus }
    ) {
      userId
      operation
      bonus
      pointsBefore
      pointsAfter
    }
  }
`

const FacilFab = () => {
  const [open, setOpen] = useState(false)
  const [list, setList] = useState([])
  const [currentUser, setCurrentUser] = useState(null)
  const [pointsUpdating, setPointsUpdating] = useState(false)
  const [getUserData, { loading: userDataLoading, data: userData }] =
    useLazyQuery(GET_USERS, {
      fetchPolicy: 'no-cache',
    })
  const [getInviteData, { loading: inviteDataLoading, data: inviteData }] =
    useLazyQuery(GET_INVITES, {
      fetchPolicy: 'no-cache',
    })
  const [updateUserPointsMutation] = useMutation(UPDATE_USER_POINTS_MUTATION)
  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: (d) => {
          return `${d.firstName} ${d.lastName}`.trim()
        },
        sortType: 'alphanumericFalsyLast',
      },
      {
        Header: 'Email',
        accessor: (d) => d.email,
        sortType: 'alphanumericFalsyLast',
      },
    ],
    []
  )
  // https://github.com/tannerlinsley/react-table/discussions/2371#discussioncomment-210260
  const sortTypes = useMemo(
    () => ({
      alphanumericFalsyLast(rowA, rowB, columnId, desc) {
        if (!rowA.values[columnId] && !rowB.values[columnId]) {
          return 0
        }

        if (!rowA.values[columnId]) {
          return desc ? -1 : 1
        }

        if (!rowB.values[columnId]) {
          return desc ? 1 : -1
        }

        return rowA.values[columnId].localeCompare(rowB.values[columnId])
      },
    }),
    []
  )
  const table = useTable(
    {
      columns,
      data: list,
      sortTypes,
    },
    useFilters
  )

  useEffect(() => {
    if (!open) {
      return
    }

    getUserData()
    getInviteData()
  }, [open, getUserData, getInviteData])

  useEffect(() => {
    if (!inviteData || !userData || inviteDataLoading || userDataLoading) {
      return
    }

    const inviteList = inviteData?.invites?.edges
      ?.map((edge) => {
        return {
          email: edge.node.email,
          firstName: '',
          lastName: '',
          points: '',
          role: edge.node.role,
          registered: false,
        }
      })
      .map((account) => {
        let newAccount = Object.assign({}, account)
        const user = userData?.users?.edges?.find(
          (u) => u.node.email.toUpperCase() === account.email.toUpperCase()
        )

        if (user !== undefined) {
          newAccount.databaseId = user.node.databaseId
          newAccount.firstName = user.node.firstName
          newAccount.lastName = user.node.lastName
          newAccount.points = user.node.points
          newAccount.registered = true
        }

        return newAccount
      })
      .filter(
        (account) => account.role === ROLE_PARTICIPANT && account.registered
      )

    setList(inviteList)
  }, [inviteData, userData, inviteDataLoading, userDataLoading])

  // Modal
  const handleClose = () => {
    setCurrentUser(null)
  }

  // Handle add point button click
  const handleAddPointClick = (email) => {
    // Look up the info
    const user = list.find((l) => l.email === email)

    if (user === undefined) {
      return
    }

    setCurrentUser(user)
  }

  const handleUpdate = async (data, updatePoint) => {
    if (!data || data.databaseId.length === 0) {
      return
    }

    if (isNaN(parseInt(updatePoint))) {
      return
    }

    try {
      setPointsUpdating(true)
      await updateUserPointsMutation({
        variables: {
          userId: data.databaseId,
          operation: 'add',
          bonus: parseInt(updatePoint),
        },
      })

      // Update success. Close the modal.
      handleClose()

      const name = `${data.firstName} ${data.lastName}`.trim()
      toast.success(`You've just added ${updatePoint} points to ${name}`)
    } catch (err) {
      // Show toast error notification
      toast.error(
        `Unable to update the participant's point. Please try again!`,
        {
          autoClose: false,
        }
      )
      console.log('Update User Points failed', err)
    } finally {
      setPointsUpdating(false)
    }
  }

  return (
    <div>
      <FacilAddPointsDialog
        data={currentUser}
        inprogress={pointsUpdating}
        title={`Add Remarkable Manager points to<br/><strong>${`${
          currentUser?.firstName ?? ''
        } ${currentUser?.lastName ?? ''}`.trim()}</strong>`}
        onClose={handleClose}
        onConfirm={handleUpdate}
      />
      <Popover>
        {({ open }) => (
          <>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 invisible -z-1  translate-y-1"
              enterTo="opacity-100 visible z-50 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 visible z-50 translate-y-0"
              leaveTo="opacity-0 invisible -z-1 translate-y-1"
            >
              <Popover.Panel className="fixed bottom-[40px] right-[75px] h-3/4 max-h-[90vh] max-w-[400px] rounded-[24px] bg-bright-gray shadow-lg">
                <div className="mx-[50px] my-[35px]">
                  <p className="font-bold text-flame">
                    Who would you like to award Remarkable Manager points to?
                  </p>
                  {/** Search name filter input */}
                  <SearchNameFilter setFilter={table.setFilter} />
                </div>
                <div className="mx-[11px] mb-[10px] overflow-hidden rounded-[11px] bg-white">
                  <div className="h-full overflow-y-auto">
                    <Table
                      table={table}
                      data={list}
                      loading={inviteDataLoading | userDataLoading}
                      handleAddPointClick={handleAddPointClick}
                    />
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
            <Popover.Button
              className="fixed bottom-[15px] right-[15px] z-50 h-[48px] w-[48px] rounded-[32px] shadow-sm"
              onClick={() => setOpen(!open)}
            >
              {open ? <FabCloseIcon /> : <FabStarIcon />}
            </Popover.Button>
          </>
        )}
      </Popover>
    </div>
  )
}

function SearchNameFilter({ setFilter }) {
  const inputRef = useRef(null)
  const [value, setValue] = useState('')

  useEffect(() => {
    if (inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 300)
    }
  }, [])

  return (
    <input
      value={value || ''}
      onChange={(e) => {
        setValue(e.target.value)
        setFilter('Name', e.target.value || undefined) // Set undefined to remove the filter entirely
      }}
      type="text"
      placeholder="Start typing a name here..."
      className="my-4 mr-4 w-full rounded-[4px] border-light-gray py-1 text-xs placeholder-dark-gray"
      ref={inputRef}
    />
  )
}

function Table({ table, loading, handleAddPointClick }) {
  const { getTableProps, getTableBodyProps, rows, prepareRow } = table

  return (
    <table className="w-full" {...getTableProps()}>
      <tbody {...getTableBodyProps()}>
        {rows.map((row) => {
          prepareRow(row)
          const rowProps = row.getRowProps()

          return (
            // Use a Fragment here so the table markup is still valid
            <Fragment key={rowProps.key}>
              <ParticipantRow
                row={row}
                rowProps={rowProps}
                loading={loading}
                onAddPointClick={handleAddPointClick}
              />
            </Fragment>
          )
        })}
      </tbody>
    </table>
  )
}

function ParticipantRow({ row, rowProps, onAddPointClick, loading }) {
  return (
    <tr
      className="mx-[10px] flex h-[49px] items-center justify-between border-b-[1px] border-b-american-silver last:border-b-0"
      {...rowProps}
    >
      {loading ? (
        <td colSpan={2}>Loading...</td>
      ) : (
        <>
          {row.cells
            .filter((cell) => cell.column.id === 'Name')
            .map((cell) => {
              return (
                <td {...cell.getCellProps()}>
                  {/** Name */}
                  {cell.render('Cell')}
                </td>
              )
            })}
          <td>
            {/** Action button */}
            <button
              type="button"
              className="h-[24px] w-[24px]"
              onClick={() => onAddPointClick(row.values.Email)}
            >
              <AddCircleIcon />
            </button>
          </td>
        </>
      )}
    </tr>
  )
}

export default FacilFab
